import type { ReactNode } from 'react';
import { type ArticleWithSlug } from '~/lib/articles';
// import { formatDate } from '~/lib/formatDate';
import { BackButton } from './BackButton';
import { Container } from './Container';

import { formatDate } from '~/lib/formatDate';
import { Article } from './Article';
import Markdown from 'markdown-to-jsx';
import slugify from '@sindresorhus/slugify';

type Media = {
  id: number;
  type: string;
  url: string;
};

type ArticleMeta = {
  readingTime: string;
};

type Section = {
  content: string;
  id: number;
  pageId: number | null;
  type: string | null;
  format: string | null;
  url: string | null;
  order: number;
};

type Author = {
  name: string;
  href?: string;
  imageUrl?: string | null;
};

function Author({
  author,
  date,
  datetime,
  readingTime,
}: {
  author: Author;
  date: string;
  datetime: string;
  readingTime?: string;
}) {
  return (
    <div className="mt-8 flex items-center">
      <div className="flex-shrink-0">
        {author.href ? (
          <a href={author.href}>
            <span className="sr-only">{author.name}</span>
            {author.imageUrl && (
              <img
                className="h-10 w-10 rounded-full bg-zinc-300 ring-2 ring-zinc-100"
                src={author.imageUrl}
                alt={author.name}
              />
            )}
          </a>
        ) : (
          author.imageUrl && (
            <img
              className="h-10 w-10 rounded-full bg-zinc-300 ring-2 ring-zinc-100"
              src={author.imageUrl}
              alt={author.name}
            />
          )
        )}
      </div>
      <div className="ml-3 text-sm leading-6">
        {/* <p className="text-sm font-medium text-gray-900"> */}
        <p className="font-semibold text-gray-900">
          <a href={author.href}>{author.name}</a>
        </p>
        <div className="flex space-x-1 text-sm text-gray-600">
          <time dateTime={datetime}>{date}</time>
          {/* Reading time */}
          {readingTime ? (
            <>
              <span aria-hidden="true">&middot;</span>
              <span>{readingTime} read</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SectionComp({ section }: { section: Section }) {
  const { type, format } = section;
  if (type === 'text') {
    if (format !== 'md') {
      throw new Error('MD is the only supported text atm.');
    }

    return (
      <Article.Content>
        <Markdown>{section.content}</Markdown>
      </Article.Content>
    );
  }
  if (type === 'image') {
    if (!section.url) {
      throw new Error('Missing Url on image.');
    }
    return (
      <Article.Image alt={slugify(section.content)} src={section.url}>
        {section.content}
      </Article.Image>
    );
  }

  throw new Error('Not Supported.');
}

export function ArticleLayout({
  article,
  sections,
  media,
  meta,
  children,
}: {
  article: ArticleWithSlug;
  sections: Section[];
  media: Media[];
  meta: ArticleMeta | null;
  children?: ReactNode;
}) {
  const authors = article.authors;

  const heroImage = media ? media[0] : null;

  return (
    <Container className="mt-16 lg:mt-32">
      <div className="xl:relative">
        <div className="mx-auto max-w-2xl">
          <BackButton />
          <Article>
            <header className="flex flex-col">
              {/* <div className="absolute inset-0">
                <img
                  alt=""
                  src="https://images.unsplash.com/photo-1525130413817-d45c1d127c42?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1920&q=60&&sat=-100"
                  className="h-full w-full object-cover"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 mix-blend-multiply"
                />
              </div> */}
              {/* {media?.map(media => (
                <div key={media.id} aria-hidden="true" className="relative">
                  <img
                    alt=""
                    src={media.url}
                    className="h-96 w-full rounded-lg object-cover object-center"
                  /> */}
              {/* <div className="absolute inset-0 bg-gradient-to-t from-white dark:bg-gradient-to-t dark:from-black" /> */}
              {/* </div>
              ))} */}
              {heroImage && (
                <div key={heroImage.id}>
                  <img
                    alt=""
                    src={heroImage.url}
                    className="h-full w-full rounded-lg object-cover object-center"
                  />
                  {/* <div
                    aria-hidden="true"
                    className="absolute inset-0 mix-blend-multiply"
                  /> */}
                </div>
              )}
              <h1 className="relative mx-auto mt-8 bg-transparent text-4xl font-bold tracking-tight text-zinc-800 sm:text-5xl dark:text-zinc-100">
                {article.title}
              </h1>
              {authors.map(author => (
                // TODO: Rename this meta and then add reading time and extract Author
                <Author
                  key={author.name}
                  author={author}
                  date={formatDate(article.date || '')}
                  datetime={article.date?.toString() || ''}
                  readingTime={meta?.readingTime}
                />
              ))}

              {/* { article.date && (
              <time
                dateTime={article.date}
                className="order-first flex items-center text-base text-zinc-400 dark:text-zinc-500"
              >
                <span className="h-4 w-0.5 rounded-full bg-zinc-200 dark:bg-zinc-500" />                
                  <span className="ml-3">{formatDate(article.date)}</span>
                )}
              </time> */}
            </header>
            <div className="mt-8">
              {/* Content */}
              {sections?.map(section => (
                <SectionComp key={section.id} section={section} />
              ))}
            </div>
          </Article>
          {children}
        </div>
      </div>
    </Container>
  );
}
